import React, { useState, useEffect } from 'react';
import { Send, Settings, Mail, Clock } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { toast } from 'sonner';
import { useT } from '../context/LanguageContext';

export default function Herinneringen() {
  const { t } = useT();
  const [cfg, setCfg] = useState({ autoHerinneringen: false, herinneringDagen: [14, 30, 45], lastReminderRun: null });
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState([]);

  const loadLog = () => carsysApi.getMailLog()
    .then(ml => setLog((ml || []).filter(m => (m.type || '').includes('herinnering')).slice(0, 30)))
    .catch(() => {});

  useEffect(() => {
    carsysApi.getReminderSettings().then(setCfg).catch(() => {});
    loadLog();
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const saved = await carsysApi.setReminderSettings(next);
      setCfg(saved);
      toast.success(t('Instellingen opgeslagen', 'Instellingen opgeslagen'));
    } catch (e) { toast.error(t('Opslaan mislukt', 'Opslaan mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const run = async () => {
    setRunning(true);
    try {
      const res = await carsysApi.runReminders();
      if (res.sent > 0) toast.success(`${res.sent} ${t('herinnering(en) verstuurd', 'herinnering(en) verstuurd')}`);
      else toast.info(t('Geen vervallen herinneringen om te versturen', 'Geen vervallen herinneringen om te versturen'));
      carsysApi.getReminderSettings().then(setCfg).catch(() => {});
      loadLog();
    } catch (e) { toast.error(t('Versturen mislukt', 'Versturen mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setRunning(false); }
  };

  return (
    <div data-testid="herinneringen-page">
      <div className="cs-page-header">
        <div><h2>{t('Automatische betalingsherinneringen', 'Automatische betalingsherinneringen')}</h2><p>{t('Stel in wanneer klanten automatisch een herinnering ontvangen voor openstaande facturen', 'Stel in wanneer klanten automatisch een herinnering ontvangen voor openstaande facturen')}</p></div>
        <button className="cs-btn primary" data-testid="btn-run-reminders" disabled={running} onClick={run}><Send size={14} /> {running ? t('Bezig...', 'Bezig...') : t('Herinneringen nu versturen', 'Herinneringen nu versturen')}</button>
      </div>

      <div className="cs-card padded" style={{ marginBottom: 14 }} data-testid="reminder-settings-card">
        <h3 className="cs-card-title"><Settings size={16} /> {t('Instellingen', 'Instellingen')}</h3>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, marginBottom: 12 }}>
          <input type="checkbox" data-testid="reminder-auto-toggle" checked={!!cfg.autoHerinneringen} onChange={e => save({ ...cfg, autoHerinneringen: e.target.checked })} />
          <b>{t('Verstuur automatisch herinneringen', 'Verstuur automatisch herinneringen')}</b> — {t('het systeem mailt klanten automatisch zodra een factuur vervalt', 'het systeem mailt klanten automatisch zodra een factuur vervalt')}
        </label>
        <div style={{ fontSize: 12.5, color: 'var(--cs-text-muted)', marginBottom: 6 }}>{t('Herinneringen versturen na (dagen sinds factuurdatum):', 'Herinneringen versturen na (dagen sinds factuurdatum):')}</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'end', flexWrap: 'wrap' }}>
          {[0, 1, 2].map(i => (
            <div key={i}>
              <label className="cs-section-title">{i + 1}e {t('herinnering', 'herinnering')}</label>
              <input type="number" className="cs-input" data-testid={`reminder-dag-${i}`} style={{ width: 90 }} min={1}
                value={cfg.herinneringDagen?.[i] ?? ''}
                onChange={e => { const d = [...(cfg.herinneringDagen || [14, 30, 45])]; d[i] = parseInt(e.target.value) || 0; setCfg({ ...cfg, herinneringDagen: d }); }} />
            </div>
          ))}
          <button className="cs-btn primary" data-testid="reminder-save-dagen" disabled={saving} onClick={() => save({ ...cfg, herinneringDagen: (cfg.herinneringDagen || []).filter(d => d > 0) })}>{t('Opslaan', 'Opslaan')}</button>
        </div>
        {cfg.lastReminderRun && (
          <div style={{ fontSize: 11.5, color: 'var(--cs-text-muted)', marginTop: 12 }}>
            <Clock size={11} style={{ verticalAlign: '-1px' }} /> {t('Laatste run', 'Laatste run')}: {new Date(cfg.lastReminderRun).toLocaleString('nl-NL')}
          </div>
        )}
        <p style={{ fontSize: 11, color: 'var(--cs-text-muted)', marginTop: 8 }}>
          {t('Elke klant ontvangt per niveau maximaal 1 e-mail (gebundeld over alle openstaande facturen). Klanten zonder e-mailadres worden overgeslagen.', 'Elke klant ontvangt per niveau maximaal 1 e-mail (gebundeld over alle openstaande facturen). Klanten zonder e-mailadres worden overgeslagen.')}
        </p>
      </div>

      <div className="cs-card">
        <h3 className="cs-card-title" style={{ padding: '12px 14px 0' }}><Mail size={16} /> {t('Verstuurde herinneringen', 'Verstuurde herinneringen')}</h3>
        <table className="cs-table">
          <thead><tr><th>{t('Datum', 'Datum')}</th><th>{t('Aan', 'Aan')}</th><th>{t('Onderwerp', 'Onderwerp')}</th><th>{t('Type', 'Type')}</th><th>{t('Status', 'Status')}</th></tr></thead>
          <tbody>
            {log.map(m => (
              <tr key={m.id} data-testid={`herinnering-log-${m.id}`}>
                <td>{m.datum}</td>
                <td>{m.aan}</td>
                <td style={{ color: 'var(--cs-text-muted)', fontSize: 12 }}>{m.onderwerp}</td>
                <td><span className={`cs-badge ${m.type === 'auto-herinnering' ? 'blue' : 'gray'}`}>{m.type === 'auto-herinnering' ? t('automatisch', 'automatisch') : t('handmatig', 'handmatig')}</span></td>
                <td style={{ fontSize: 12 }}>{m.status}</td>
              </tr>
            ))}
            {log.length === 0 && <tr><td colSpan={5} className="cs-empty">{t('Nog geen herinneringen verstuurd.', 'Nog geen herinneringen verstuurd.')}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
